// src/components/setup/steps/ShopCreation.jsx
import React, { useState, useEffect } from 'react';
import { Store, Plus, Package, Truck, Check, X, MapPin, Phone, Edit3, Banknote } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { createShop, checkOrderPrefix } from '../../../services/api/shops';
import { getShippingZones } from '../../../services/api/shipping';
import { getBusinessProfile } from '../../../services/api/business';
import styles from './StepComponents.module.css';

const ShopCreation = ({ user, formData, setFormData, onNext, isSubmitting }) => {
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [shopData, setShopData] = useState({
    name: '',
    type: 'online',
    orderNumberPrefix: '',
    address: '',
    phone: '',
    deliveryCharges: []
  });
  const [shippingZones, setShippingZones] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingZones, setIsLoadingZones] = useState(true);
  const [isCheckingPrefix, setIsCheckingPrefix] = useState(false);
  const [prefixStatus, setPrefixStatus] = useState(null);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditValue, setBulkEditValue] = useState('');

  // Shop types with translations matching CreateShopPage
  const shopTypes = [
    { 
      value: 'online', 
      label: t('shops.online', 'Online Store'), 
      icon: Store,
      description: t('shops.onlineDescription', 'Sell products online with delivery options')
    },
    { 
      value: 'pos', 
      label: t('shops.pos', 'POS Store'), 
      icon: Package,
      description: t('shops.posDescription', 'Physical store with point-of-sale system')
    },
    { 
      value: 'dropshipper', 
      label: t('shops.dropshipper', 'Dropshipper'), 
      icon: Truck,
      description: t('shops.dropshipperDescription', 'Sell products with supplier fulfillment')
    }
  ];

  // Load shipping zones and prefill business data
  useEffect(() => {
    fetchShippingZones();
    prefillBusinessData();
  }, []);

  // Initialize delivery charges when zones are loaded
  useEffect(() => {
    if (shippingZones.length > 0 && shopData.deliveryCharges.length === 0) {
      setShopData(prev => ({
        ...prev,
        deliveryCharges: shippingZones.map(zone => ({
          shippingZoneId: zone.id,
          amount: ''
        }))
      }));
    }
  }, [shippingZones]);

  // Debounced prefix validation
  useEffect(() => {
    if (!shopData.orderNumberPrefix || shopData.orderNumberPrefix.length < 2) {
      setPrefixStatus(null);
      setErrors(prev => ({ ...prev, orderNumberPrefix: '' }));
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsCheckingPrefix(true);
        const response = await checkOrderPrefix(shopData.orderNumberPrefix);
        setPrefixStatus(response.taken ? 'taken' : 'available');
        
        if (response.taken) {
          setErrors(prev => ({ 
            ...prev, 
            orderNumberPrefix: t('shops.prefixTaken', 'This prefix is already taken')
          }));
        } else {
          setErrors(prev => ({ ...prev, orderNumberPrefix: '' }));
        }
      } catch (err) {
        console.error('Failed to check prefix:', err);
        setPrefixStatus(null);
      } finally {
        setIsCheckingPrefix(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [shopData.orderNumberPrefix, t]);

  const fetchShippingZones = async () => {
    try {
      setIsLoadingZones(true);
      const response = await getShippingZones();
      setShippingZones(response);
    } catch (err) {
      console.error('Failed to fetch shipping zones:', err);
      error(t('shops.failedToLoadZones', 'Failed to load shipping zones'));
    } finally {
      setIsLoadingZones(false);
    }
  };

  const prefillBusinessData = async () => {
    try {
      // Try to get business profile data to prefill address and phone
      const response = await getBusinessProfile();
      const business = response.data;
      
      setShopData(prev => ({
        ...prev,
        address: business.businessAddress || '',
        phone: business.businessPhone || ''
      }));
    } catch (err) {
      // Fallback to user business data if profile fetch fails
      if (user?.business) {
        setShopData(prev => ({
          ...prev,
          address: user.business.businessAddress || user.business.address || '',
          phone: user.business.businessPhone || user.business.phone || ''
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!shopData.name.trim()) {
      newErrors.name = t('shops.nameRequired', 'Shop name is required');
    } else if (shopData.name.trim().length < 2) {
      newErrors.name = t('shops.nameMinLength', 'Shop name must be at least 2 characters');
    }

    if (!shopData.orderNumberPrefix.trim()) {
      newErrors.orderNumberPrefix = t('shops.prefixRequired', 'Order prefix is required');
    } else if (shopData.orderNumberPrefix.length < 2) {
      newErrors.orderNumberPrefix = t('shops.prefixMinLength', 'Prefix must be at least 2 characters');
    } else if (prefixStatus === 'taken') {
      newErrors.orderNumberPrefix = t('shops.prefixTaken', 'This prefix is already taken');
    }

    // Phone validation (if provided)
    if (shopData.phone && !/^[0-9+\-\s()]+$/.test(shopData.phone)) {
      newErrors.phone = t('shops.invalidPhone', 'Invalid phone number format');
    }

    // Validate delivery charges for online and dropshipper shops
    if (shopData.type === 'online' || shopData.type === 'dropshipper') {
      const hasEmptyCharges = shopData.deliveryCharges.some(charge => 
        charge.amount === '' || charge.amount < 0
      );
      if (hasEmptyCharges) {
        newErrors.deliveryCharges = t('shops.deliveryChargesRequired', 'All delivery charges must be filled');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShopData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePrefixChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5);
    setShopData(prev => ({
      ...prev,
      orderNumberPrefix: value
    }));

    if (errors.orderNumberPrefix) {
      setErrors(prev => ({
        ...prev,
        orderNumberPrefix: ''
      }));
    }
  };

  const handleTypeChange = (type) => {
    setShopData(prev => ({
      ...prev,
      type
    }));

    if (errors.type) {
      setErrors(prev => ({
        ...prev,
        type: ''
      }));
    }
  };

  const handleDeliveryChargeChange = (zoneId, amount) => {
    setShopData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge =>
        charge.shippingZoneId === zoneId
          ? { ...charge, amount: amount === '' ? '' : parseFloat(amount) }
          : charge
      )
    }));

    if (errors.deliveryCharges) {
      setErrors(prev => ({
        ...prev,
        deliveryCharges: ''
      }));
    }
  };

  const handleBulkEditOpen = () => {
    setBulkEditValue('');
    setShowBulkEditModal(true);
  };

  const handleBulkEditApply = () => {
    if (bulkEditValue === '' || isNaN(bulkEditValue) || parseFloat(bulkEditValue) < 0) {
      error(t('shops.invalidBulkEditValue', 'Please enter a valid amount'));
      return;
    }

    setShopData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge => ({
        ...charge,
        amount: parseFloat(bulkEditValue)
      }))
    }));

    if (errors.deliveryCharges) {
      setErrors(prev => ({
        ...prev,
        deliveryCharges: ''
      }));
    }

    setShowBulkEditModal(false);
    success(t('shops.bulkEditApplied', 'Bulk edit applied to all zones'));
  };

  const handleSetFreeDeliveryAll = () => {
    setShopData(prev => ({
      ...prev,
      deliveryCharges: prev.deliveryCharges.map(charge => ({
        ...charge,
        amount: 0
      }))
    }));

    if (errors.deliveryCharges) {
      setErrors(prev => ({
        ...prev,
        deliveryCharges: ''
      }));
    }

    success(t('shops.freeDeliveryApplied', 'Free delivery applied to all zones'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsCreating(true);

    try {
      // Prepare form data
      const submitData = new FormData();
      submitData.append('name', shopData.name.trim());
      submitData.append('type', shopData.type);
      submitData.append('orderNumberPrefix', shopData.orderNumberPrefix.trim());
      
      if (shopData.address.trim()) {
        submitData.append('address', shopData.address.trim());
      }
      
      if (shopData.phone.trim()) {
        submitData.append('phone', shopData.phone.trim());
      }

      // Add logged-in user to userIds to automatically assign them to the shop
      const userIds = [user.id];
      submitData.append('userIds', JSON.stringify(userIds));

      // Filter out empty delivery charges
      if (shopData.type === 'online' || shopData.type === 'dropshipper') {
        const validDeliveryCharges = shopData.deliveryCharges
          .filter(charge => charge.amount !== '' && charge.amount >= 0)
          .map(charge => ({
            shippingZoneId: charge.shippingZoneId,
            amount: parseFloat(charge.amount)
          }));
        submitData.append('deliveryCharges', JSON.stringify(validDeliveryCharges));
      }

      const response = await createShop(submitData);
      
      // Update form data for next steps
      setFormData(prev => ({
        ...prev,
        shop: response.shop
      }));

      success(t('shops.shopCreatedSuccess', 'Shop "{shopName}" created successfully', { 
        shopName: response.shop.name 
      }));
      onNext();
    } catch (err) {
      console.error('Shop creation failed:', err);
      if (err.response?.data?.message) {
        error(err.response.data.message);
      } else {
        error(t('shops.failedToCreate', 'Failed to create shop'));
      }
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoadingZones) {
    return (
      <div className={styles.stepContainer}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <h3>{t('shops.loadingData', 'Loading shop data...')}</h3>
          <p>{t('shops.loadingDataDesc', 'Please wait while we prepare the shop creation form.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <Store className={styles.stepIcon} size={48} />
        <h2>{t('shops.createNewShop', 'Create Your First Shop')}</h2>
        <p>{t('shops.setupYourShop', 'Set up your shop to start selling products and managing orders.')}</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.stepForm}>
        {/* Shop Name */}
        <div className={styles.formGroup}>
          <label htmlFor="name">{t('shops.shopName', 'Shop Name')} *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={shopData.name}
            onChange={handleInputChange}
            placeholder={t('shops.shopNamePlaceholder', 'Enter your shop name')}
            className={errors.name ? styles.error : ''}
            required
            autoFocus
          />
          {errors.name && <span className={styles.errorText}>{errors.name}</span>}
        </div>

        {/* Shop Type */}
        <div className={styles.formGroup}>
          <label>{t('shops.shopType', 'Shop Type')} *</label>
          <div className={styles.shopTypeGrid}>
            {shopTypes.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.value}
                  type="button"
                  className={`${styles.shopTypeCard} ${shopData.type === type.value ? styles.selected : ''}`}
                  onClick={() => handleTypeChange(type.value)}
                >
                  <Icon size={24} className={styles.typeIcon} />
                  <span className={styles.typeLabel}>{type.label}</span>
                  <span className={styles.typeDescription}>{type.description}</span>
                </button>
              );
            })}
          </div>
          {errors.type && <span className={styles.errorText}>{errors.type}</span>}
        </div>

        {/* Order Number Prefix */}
        <div className={styles.formGroup}>
          <label htmlFor="orderNumberPrefix">{t('shops.orderPrefix', 'Order Number Prefix')} *</label>
          <div className={styles.prefixInputWrapper}>
            <input
              type="text"
              id="orderNumberPrefix"
              value={shopData.orderNumberPrefix}
              onChange={handlePrefixChange}
              placeholder="ABC"
              className={`${styles.prefixInput} ${errors.orderNumberPrefix ? styles.error : ''} ${prefixStatus === 'available' ? styles.success : ''}`}
              maxLength="5"
              required
            />
            <div className={styles.prefixStatus}>
              {isCheckingPrefix && <div className={styles.checkingSpinner}></div>}
              {!isCheckingPrefix && prefixStatus === 'available' && (
                <Check size={16} className={`${styles.statusIcon} ${styles.success}`} />
              )}
              {!isCheckingPrefix && prefixStatus === 'taken' && (
                <X size={16} className={`${styles.statusIcon} ${styles.error}`} />
              )}
            </div>
          </div>
          {errors.orderNumberPrefix && <span className={styles.errorText}>{errors.orderNumberPrefix}</span>}
          {!errors.orderNumberPrefix && prefixStatus === 'available' && (
            <span className={styles.successText}>{t('shops.prefixAvailable', 'Prefix is available')}</span>
          )}
          <span className={styles.formHint}>
            {t('shops.prefixHint', 'This prefix will be used for order numbers (e.g., ABC-001)')}
          </span>
        </div>

        {/* Shop Address and Phone */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="address">{t('shops.address', 'Shop Address')}</label>
            <div className={styles.inputWithIcon}>
              <MapPin className={styles.inputIcon} size={18} />
              <input
                type="text"
                id="address"
                name="address"
                value={shopData.address}
                onChange={handleInputChange}
                placeholder={t('shops.addressPlaceholder', 'Enter shop address')}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="phone">{t('shops.phone', 'Shop Phone')}</label>
            <div className={styles.inputWithIcon}>
              <Phone className={styles.inputIcon} size={18} />
              <input
                type="tel"
                id="phone"
                name="phone"
                value={shopData.phone}
                onChange={handleInputChange}
                placeholder={t('shops.phonePlaceholder', 'Enter shop phone number')}
                className={errors.phone ? styles.error : ''}
              />
            </div>
            {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
          </div>
        </div>

        {/* Delivery Charges - Only for online and dropshipper shops */}
        {(shopData.type === 'online' || shopData.type === 'dropshipper') && (
          <div className={styles.formGroup}>
            <div className={styles.deliveryChargesHeader}>
              <label>{t('shops.deliveryCharges', 'Delivery Charges')} *</label>
              <div className={styles.bulkActions}>
                <button
                  type="button"
                  onClick={handleBulkEditOpen}
                  className={styles.bulkEditButton}
                >
                  <Edit3 size={16} />
                  {t('shops.bulkEdit', 'Bulk Edit')}
                </button>
                <button
                  type="button"
                  onClick={handleSetFreeDeliveryAll}
                  className={styles.freeDeliveryButton}
                >
                  <Package size={16} />
                  {t('shops.setFreeDeliveryAll', 'Free Delivery All')}
                </button>
              </div>
            </div>
            <div className={styles.deliveryChargesGrid}>
              {shippingZones.map((zone) => {
                const charge = shopData.deliveryCharges.find(c => c.shippingZoneId === zone.id);
                return (
                  <div key={zone.id} className={styles.chargeInputGroup}>
                    <label htmlFor={`charge-${zone.id}`}>{zone.name}</label>
                    <div className={styles.inputWithIcon}>
                      <span className={styles.currencyIcon}>{user?.business?.currency || 'LKR'}</span>
                      <input
                        type="number"
                        id={`charge-${zone.id}`}
                        value={charge?.amount === 0 ? 0 : (charge?.amount || '')}
                        onChange={(e) => handleDeliveryChargeChange(zone.id, e.target.value)}
                        placeholder="150.00"
                        min="0"
                        step="0.01"
                        className={`${styles.deliveryInput} ${errors.deliveryCharges ? styles.error : ''}`}
                        required
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {errors.deliveryCharges && <span className={styles.errorText}>{errors.deliveryCharges}</span>}
            <span className={styles.formHint}>
              {t('shops.deliveryChargesHint', 'Set delivery charges for each shipping zone. Use 0 for free delivery.')}
            </span>
          </div>
        )}

        <div className={styles.stepActions}>
          <button
            type="submit"
            disabled={isCreating || isSubmitting || prefixStatus === 'taken'}
            className={styles.primaryButton}
          >
            {isCreating ? (
              <>
                <div className={styles.loadingSpinner}></div>
                {t('shops.creating', 'Creating...')}
              </>
            ) : (
              <>
                <Store size={20} />
                {t('shops.createShop', 'Create Shop')}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className={styles.bulkEditOverlay}>
          <div className={styles.bulkEditModal}>
            <div className={styles.modalHeader}>
              <h3>{t('shops.bulkEditDeliveryCharges', 'Bulk Edit Delivery Charges')}</h3>
              <button onClick={() => setShowBulkEditModal(false)} className={styles.closeButton}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label htmlFor="bulkEditValue">
                  {t('shops.deliveryChargeAmount', 'Delivery Charge Amount')}
                </label>
                <div className={styles.inputWithIcon}>
                  <Banknote className={styles.inputIcon} size={18} />
                  <input
                    type="number"
                    id="bulkEditValue"
                    value={bulkEditValue}
                    onChange={(e) => setBulkEditValue(e.target.value)}
                    placeholder="150.00"
                    min="0"
                    step="0.01"
                    autoFocus
                  />
                  <span className={styles.currencyIcon}>{user?.business?.currency || 'LKR'}</span>
                </div>
                <span className={styles.formHint}>
                  {t('shops.bulkEditHint', 'This amount will be applied to all shipping zones')}
                </span>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => setShowBulkEditModal(false)}
                className={styles.secondaryButton}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleBulkEditApply}
                className={styles.primaryButton}
              >
                <Check size={16} />
                {t('shops.applyToAll', 'Apply to All')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopCreation;